import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { env } from '@/config/env';
import { nearbyOblasts, type OblastInfo } from '@/modules/geolocation';
import { useGeolocationStore } from '@/modules/geolocation';

/**
 * Per-oblast alert state as served by the backend proxy (legacy projection of the
 * aggregated alerts.in.ua + UkraineAlarm snapshot). Ключ — точное upstream-имя
 * области (Харківська область, м. Київ), value — флаг тревоги + момент изменения.
 */
export interface OblastAlertState {
  alertnow: boolean;
  changed: string;
}

/**
 * Снапшот тревог от backend (`GET /api/alerts` / socket.io `alerts-update`).
 * Новый агрегированный контракт: legacy-проекция (обратная совместимость со старым
 * ubilling-форматом) + threats (авиа-угрозы) + targets (точечные цели с lat/lon).
 * Большинство consumer-ов пока работает только с `legacy.states`.
 */
export interface AlertsSnapshot {
  legacy: {
    states: Record<string, OblastAlertState>;
    cachedat: string | null;
    fetchedAt: number | null;
  };
  threats: {
    hasBallistics: boolean;
    hasStrategicAviation: boolean;
    hasTacticalAviation: boolean;
    hasBoats: boolean;
    lastUpdate: number | null;
  } | null;
  targets: Array<{ lat: number; lon: number; type: string; lastSeen: number }>;
  meta: { fetchedAt: number | null; lastUpdatedAt: number | null; sources: string[] };
}

/** Snapshots older than this (by our own clock) are ignored — fail back to normal mode. */
const STALE_AFTER_MS = 10 * 60_000;
/** How often the staleness clock re-evaluates. */
const STALE_TICK_MS = 30_000;

/**
 * Air-raid alert status around the user. `alertActive` is tri-state:
 *  - true  — an alert is on in an oblast within ~50 km of the user;
 *  - false — fresh data, oblasts around the user known, no alert;
 *  - null  — unknown (no data yet, data stale, or no GPS fix) ⇒ callers must
 *            behave as if power saving were off: missing an explosion is worse
 *            than spending battery.
 */
export const useAlertStore = defineStore('alerts', () => {
  const geolocationStore = useGeolocationStore();

  const states = ref<Record<string, OblastAlertState>>({});
  const cachedat = ref<string | null>(null);
  /** Client-clock timestamp of the last snapshot we accepted (REST or WS). */
  const lastUpdateAt = ref<number | null>(null);
  /** Авиа-угрозы общегосударственного уровня (баллистика/МиГ/страт. и такт. авиация). */
  const threats = ref<AlertsSnapshot['threats']>(null);
  /** Точечные цели (дроны/ракеты) с координатами — для слоя на радаре. */
  const targets = ref<AlertsSnapshot['targets']>([]);
  /** Ticker so `isStale` re-evaluates without any incoming events. */
  const now = ref(Date.now());
  let ticker: ReturnType<typeof setInterval> | null = null;

  const isStale = computed(
    () => lastUpdateAt.value === null || now.value - lastUpdateAt.value > STALE_AFTER_MS,
  );

  /** Oblasts within the 50 km relevance radius of the user, nearest first. */
  const relevantOblasts = computed<OblastInfo[]>(() => {
    const c = geolocationStore.userCenter;
    return c ? nearbyOblasts(c.lat, c.lon) : [];
  });

  const alertActive = computed<boolean | null>(() => {
    if (isStale.value || relevantOblasts.value.length === 0) return null;
    let known = false;
    for (const oblast of relevantOblasts.value) {
      const state = states.value[oblast.name];
      if (!state) continue;
      known = true;
      if (state.alertnow) return true;
    }
    return known ? false : null;
  });

  /** Oblast to show in the status badge: nearest alerting one. `null` when no alert is active. */
  const displayOblast = computed<string | null>(() => {
    const alerting = relevantOblasts.value.find((o) => states.value[o.name]?.alertnow);
    return alerting?.name ?? null;
  });

  function applyUpdate(snapshot: AlertsSnapshot): void {
    if (!snapshot || typeof snapshot !== 'object') return;
    // Новый агрегированный контракт несёт legacy-проекцию; поддерживаем и старый
    // плоский формат (на случай partial-deploy) ради устойчивости.
    const legacy = snapshot.legacy as AlertsSnapshot['legacy'] | undefined;
    const statesMap = legacy?.states ?? (snapshot as { states?: Record<string, OblastAlertState> }).states;
    if (!statesMap || typeof statesMap !== 'object') return;
    // A backend that hasn't completed its first upstream poll sends an empty map — not data.
    const fetchedAt = legacy?.fetchedAt ?? (snapshot as { fetchedAt?: number | null }).fetchedAt ?? snapshot.meta?.fetchedAt ?? null;
    if (fetchedAt === null) return;
    states.value = statesMap;
    cachedat.value = legacy?.cachedat ?? (snapshot as { cachedat?: string | null }).cachedat ?? null;
    threats.value = snapshot.threats ?? null;
    targets.value = snapshot.targets ?? [];
    lastUpdateAt.value = Date.now();
  }

  async function fetchSnapshot(): Promise<void> {
    try {
      const res = await fetch(`${env.apiUrl}/alerts`);
      if (!res.ok) return;
      applyUpdate((await res.json()) as AlertsSnapshot);
    } catch {
      // network failure — keep whatever we have; staleness will age it out
    }
  }

  function startTicker(): void {
    if (ticker) return;
    ticker = setInterval(() => {
      now.value = Date.now();
    }, STALE_TICK_MS);
  }

  function stopTicker(): void {
    if (ticker) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  return {
    states,
    cachedat,
    lastUpdateAt,
    isStale,
    relevantOblasts,
    alertActive,
    displayOblast,
    threats,
    targets,
    applyUpdate,
    fetchSnapshot,
    startTicker,
    stopTicker,
  };
});
